import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type NotifChannel = 'system' | 'email';
export type NotifCategory =
  | 'comunicados'
  | 'pagamentos'
  | 'cobrancas'
  | 'pendencias'
  | 'lembretes'
  | 'sistema'
  | 'plano';

export interface CategoryPrefs {
  system: boolean;
  email: boolean;
}

export type NotificationPreferences = Record<NotifCategory, CategoryPrefs>;

const DEFAULT_PREFS: NotificationPreferences = {
  comunicados: { system: true, email: false },
  pagamentos:  { system: true, email: false },
  cobrancas:   { system: true, email: false },
  pendencias:  { system: true, email: false },
  lembretes:   { system: true, email: false },
  sistema:     { system: true, email: false },
  plano:       { system: true, email: false },
};

export const CATEGORY_LABELS: Record<NotifCategory, { label: string; description: string; icon: string }> = {
  comunicados: {
    label: 'Comunicados',
    description: 'Avisos e comunicados enviados pela plataforma.',
    icon: 'ri-megaphone-line',
  },
  pagamentos: {
    label: 'Pagamentos',
    description: 'Confirmações, pendências e falhas de pagamento.',
    icon: 'ri-secure-payment-line',
  },
  cobrancas: {
    label: 'Cobranças e vencimentos',
    description: 'Lembretes de vencimento de faturas e contas.',
    icon: 'ri-bank-card-line',
  },
  pendencias: {
    label: 'Pendências',
    description: 'Lançamentos pendentes e eventos vencidos no calendário.',
    icon: 'ri-calendar-todo-line',
  },
  lembretes: {
    label: 'Lembretes automáticos',
    description: 'Alertas automáticos sobre cartões próximos ao vencimento.',
    icon: 'ri-alarm-line',
  },
  sistema: {
    label: 'Avisos do sistema',
    description: 'Informações, alertas e notificações gerais do sistema.',
    icon: 'ri-information-line',
  },
  plano: {
    label: 'Plano e assinatura',
    description: 'Atualizações, upgrades e mudanças no seu plano.',
    icon: 'ri-vip-crown-line',
  },
};

export const useNotificationPreferences = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchPrefs = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const { data } = await supabase
      .from('profiles')
      .select('notification_preferences')
      .eq('id', user.id)
      .maybeSingle();

    if (data?.notification_preferences) {
      // Merge with defaults so new categories appear automatically
      const merged = { ...DEFAULT_PREFS };
      const stored = data.notification_preferences as Partial<NotificationPreferences>;
      (Object.keys(stored) as NotifCategory[]).forEach((key) => {
        if (merged[key] !== undefined && stored[key]) {
          merged[key] = { ...merged[key], ...stored[key] };
        }
      });
      setPrefs(merged);
    } else {
      setPrefs(DEFAULT_PREFS);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const updatePref = useCallback(async (
    category: NotifCategory,
    channel: NotifChannel,
    value: boolean
  ) => {
    if (!user) return;
    // Optimistic update
    setPrefs((prev) => ({
      ...prev,
      [category]: { ...prev[category], [channel]: value },
    }));
  }, [user]);

  const savePrefs = useCallback(async (newPrefs?: NotificationPreferences) => {
    if (!user) return;
    setSaving(true);
    const toSave = newPrefs ?? prefs;
    await supabase
      .from('profiles')
      .update({ notification_preferences: toSave })
      .eq('id', user.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [user, prefs]);

  return { prefs, loading, saving, saved, updatePref, savePrefs, setPrefs };
};
